import { useState } from 'react';
import type { HermesResult } from '@lens/shared';
import { MarkdownCell } from '../../shared/fields/renderFieldValue.tsx';
import styles from './HermesResultView.module.css';

type Scenario = 'optimistic' | 'realistic' | 'pessimistic';

interface Props {
  result: HermesResult;
  /**
   * Если задан — под ячейками сценариев показывается кнопка «Записать в поля»,
   * которая пишет ВЫБРАННЫЙ сценарий (total + разбивку) в поля Lens. Не задан
   * (напр. в истории оценок) — кнопки нет.
   */
  onApplyScenario?: (scenario: Scenario) => void;
  /** Идёт запись сценария в поля — блокируем кнопку. */
  applyingScenario?: boolean;
}

const SCENARIO_LABELS: Record<Scenario, string> = {
  optimistic: 'Оптимист',
  realistic: 'Реалист',
  pessimistic: 'Пессимист',
};

const ROLE_LABELS: Record<string, string> = {
  backend: 'Бэкенд',
  design: 'Дизайн',
  designer: 'Дизайн',
  frontend: 'Фронтенд',
  analytics: 'Аналитика',
  testing: 'Тестирование',
  analyst: 'Аналитика',
  qa: 'Тестирование',
};

const ROLE_COLORS: Record<string, string> = {
  backend: '#1677ff',
  design: '#eb2f96',
  designer: '#eb2f96',
  frontend: '#52c41a',
  analytics: '#722ed1',
  analyst: '#722ed1',
  testing: '#fa8c16',
  qa: '#fa8c16',
};

const FALLBACK_COLORS = ['#1677ff', '#fa8c16', '#722ed1', '#52c41a', '#eb2f96', '#13c2c2'];

function unitLabel(unit?: string): string {
  if (!unit) return '';
  const u = unit.toLowerCase();
  if (u === 'person-days' || u === 'person_days' || u === 'man-days') return 'чел-дней';
  if (u === 'person-hours' || u === 'man-hours') return 'чел-часов';
  return unit;
}

/**
 * Числа оценки показываем целыми — дробные чел-дни читаются как ложная точность.
 * Округляем ВВЕРХ, ровно как бэкенд при записи в поля (`transformToWrite`,
 * `Math.ceil`). Пока здесь был `Math.round`, история показывала 74 там, где в
 * поле лежало 75, и эти числа переписывали в карточку руками.
 */
function roundDays(value: number): number {
  return Math.ceil(value);
}

function severityColor(sev?: string): string {
  const s = (sev ?? '').toLowerCase();
  if (s === 'high' || s === 'critical' || s === 'высокий') return '#ff4d4f';
  if (s === 'low' || s === 'низкий') return '#52c41a';
  return '#fa8c16';
}

function ListSection({ label, items, variant }: { label: string; items?: string[]; variant?: 'muted' | 'warn' }) {
  if (!items || items.length === 0) return null;
  return (
    <>
      <div className={styles.sectionLabel}>{label}</div>
      <ul className={`${styles.list} ${variant ? styles[variant] : ''}`}>
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </>
  );
}

export function HermesResultView({ result, onApplyScenario, applyingScenario }: Props): JSX.Element {
  const [showSummary, setShowSummary] = useState(false);
  // Тоггл сценария подсвечивает выбранный итог. Разбивка по ролям следует за
  // сценарием, если Hermes прислал by_role_spread (оценка по ролям на каждый
  // сценарий — ITEM-12 «полная оценка»). Иначе — единственная by_role, её сумма
  // может не совпадать с итогом (в итог заложены интеграция/буфер вне ролей),
  // показываем как есть, без выдуманных чисел.
  // Стартуем с реалиста: именно его бэкенд пишет в поля по умолчанию
  // (`extractResultPath`, scenario ?? 'realistic'). С пессимистом история
  // показывала числа, которых в полях записи нет и никогда не было.
  const [scenario, setScenario] = useState<Scenario>('realistic');
  const sol = result.solution;
  const est = result.estimate;
  const total = est?.total;
  const unit = unitLabel(est?.unit);

  const byRole = est?.by_role ?? {};
  const spread = est?.by_role_spread;
  const hasSpread = spread != null && Object.keys(spread).length > 0;
  const roleKeys = Object.keys(hasSpread ? spread : byRole);
  const roleEntries = roleKeys
    .map((role) => {
      const scen = spread?.[role]?.[scenario];
      const value = typeof scen === 'number' ? scen : byRole[role];
      return [role, value] as [string, number | undefined];
    })
    .filter((e): e is [string, number] => typeof e[1] === 'number');
  const maxRole = roleEntries.reduce((m, [, v]) => Math.max(m, v), 0) || 1;
  const roleSum = roundDays(roleEntries.reduce((s, [, v]) => s + v, 0));

  const hasSolution =
    sol &&
    (sol.title || sol.description || sol.components?.length || sol.steps?.length || sol.excluded?.length || sol.risks?.length);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      {/* ─── Как это сделать ─── */}
      {hasSolution && (
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.icon}>💡</span> Как это сделать
          </div>
          {sol?.title && <div className={styles.solTitle}>{sol.title}</div>}
          {sol?.description && (
            <div className={styles.solDesc}>
              <MarkdownCell value={sol.description} />
            </div>
          )}
          {sol?.components && sol.components.length > 0 && (
            <div className={styles.tags}>
              {sol.components.map((c, i) => (
                <span key={i} className={styles.tag}>
                  {c}
                </span>
              ))}
            </div>
          )}
          <ListSection label="Что делаем" items={sol?.steps} />
          <ListSection label="Не входит" items={sol?.excluded} variant="muted" />
          <ListSection label="Риски решения" items={sol?.risks} variant="warn" />
        </section>
      )}

      {/* ─── Риски ─── */}
      {result.risks && result.risks.length > 0 && (
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.icon}>⚠️</span> Риски
          </div>
          {result.risks.map((r, i) => (
            <div key={i} className={styles.riskRow}>
              <span className={styles.dot} style={{ background: severityColor(r.severity) }} />
              <span>{r.description}</span>
            </div>
          ))}
        </section>
      )}

      {/* ─── Оценка трудоёмкости ─── */}
      {(total || roleEntries.length > 0) && (
        <section className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.icon}>◔</span> Оценка трудоёмкости
          </div>
          {total && (
            <>
              <div className={styles.cells}>
                {([
                  ['optimistic', 'Оптимист', total.optimistic],
                  ['realistic', 'Реалист', total.realistic],
                  ['pessimistic', 'Пессимист', total.pessimistic],
                ] as Array<[Scenario, string, number | undefined]>).map(([key, label, value]) => (
                  <button
                    type="button"
                    key={key}
                    className={`${styles.cell} ${scenario === key ? styles.accent : ''}`}
                    aria-pressed={scenario === key}
                    onClick={() => setScenario(key)}
                  >
                    <div className={styles.cellLabel}>{label}</div>
                    <div className={styles.cellValue}>{value == null ? '—' : roundDays(value)}</div>
                    {unit && <div className={styles.cellUnit}>{unit}</div>}
                  </button>
                ))}
              </div>
              <div className={styles.estimateMeta}>
                {result.task_class ? (
                  <span className={styles.classTag}>Класс задачи {result.task_class}</span>
                ) : (
                  <span />
                )}
                {result.summary && (
                  <span className={styles.sourceLink} onClick={() => setShowSummary((v) => !v)}>
                    Откуда оценка →
                  </span>
                )}
              </div>
              {onApplyScenario && (
                <div className={styles.applyRow}>
                  <button
                    type="button"
                    className={styles.applyBtn}
                    disabled={applyingScenario}
                    onClick={() => onApplyScenario(scenario)}
                  >
                    {applyingScenario ? 'Записываю…' : `Записать в поля: ${SCENARIO_LABELS[scenario]}`}
                  </button>
                  <span className={styles.applyHint}>
                    в поля Lens уйдёт итог и разбивка выбранного сценария
                  </span>
                </div>
              )}
              {showSummary && result.summary && (
                <div className={styles.summaryBox}>
                  <MarkdownCell value={result.summary} />
                </div>
              )}
            </>
          )}

          {roleEntries.length > 0 && (
            <div className={styles.roles}>
              <div className={styles.sectionLabel}>
                По ролям (оценка Hermes){total && roleSum ? ` · сумма ${roleSum}${unit ? ` ${unit}` : ''}` : ''}
              </div>
              {roleEntries.map(([role, val], i) => {
                const color = ROLE_COLORS[role.toLowerCase()] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length];
                const pct = Math.max(4, Math.round(((val as number) / maxRole) * 100));
                return (
                  <div key={role} className={styles.roleRow}>
                    <span className={styles.roleName}>{ROLE_LABELS[role.toLowerCase()] ?? role}</span>
                    <span className={styles.roleTrack}>
                      <span className={styles.roleFill} style={{ width: `${pct}%`, background: color }} />
                    </span>
                    <span className={styles.roleVal}>{roundDays(val as number)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
